"use client"

import { useState } from "react"
import Link from "next/link"
import { AlertTriangle, X } from "lucide-react"
import { useClientValue } from "@/lib/hydration-utils"

const STORAGE_KEY = "tls-dismiss-firmware-2026-9-1"

interface FirmwareCalloutBannerProps {
  source: "sounds" | "guide"
}

export default function FirmwareCalloutBanner({ source }: FirmwareCalloutBannerProps) {
  // Stored dismissal flag — read via useSyncExternalStore so the server
  // snapshot is `false` (banner hidden during SSR, matching the previous
  // useEffect-mount behavior) and the client snapshot is the real
  // sessionStorage value. Avoids the setState-in-effect anti-pattern.
  const showFromStorage = useClientValue(false, () => {
    try {
      return sessionStorage.getItem(STORAGE_KEY) !== "1"
    } catch {
      return true
    }
  })
  // In-session dismissal — separate state so clicking X hides the banner
  // immediately without waiting for a re-read of sessionStorage.
  const [dismissedThisSession, setDismissedThisSession] = useState(false)

  if (!showFromStorage || dismissedThisSession) return null

  const handleDismiss = () => {
    setDismissedThisSession(true)
    try {
      sessionStorage.setItem(STORAGE_KEY, "1")
    } catch {
      // ignore
    }
  }

  return (
    <div
      // §11.1: static content that mounts once, never mutates in place — an
      // aria-live node here never actually fired (insertion of an
      // already-populated live region isn't announced by most AT) and would
      // just be a second broadcaster if it were. lib/announce.ts owns every
      // real announcement site-wide.
      data-firmware-banner=""
      className="tls-clears-nav-flush border-b border-[color:var(--tls-border-subtle)] bg-[var(--tls-surface-elevated)]"
    >
      <div className="tls-container flex items-center gap-3 py-3">
        {/* Icons are currentColor ink; accent is never used on an icon except
            playback state. The banner's urgency is carried by position, the
            date in bold, and the link, not by a hue. */}
        <AlertTriangle
          aria-hidden="true"
          className="size-4 flex-shrink-0 text-[var(--tls-fg-muted)]"
        />
        <p className="flex-1 text-sm text-[var(--tls-fg-strong)]">
          <span className="font-semibold">2026.9.1 update:</span>{" "}
          <span className="text-[var(--tls-fg-muted)]">
            Custom lock sound stopped working?
          </span>{" "}
          <Link
            prefetch={false}
            href={`/blog/tesla-lock-sound-muted-after-update-2026-9?entry=${source}_firmware_banner`}
            className="inline-flex items-center gap-1 font-medium text-[var(--tls-brand-ink)] underline underline-offset-2 hover:no-underline"
          >
            See the 2-minute fix
          </Link>
        </p>
        <button
          type="button"
          onClick={handleDismiss}
          aria-label="Dismiss firmware update notice"
          className="flex min-h-[44px] min-w-[44px] flex-shrink-0 items-center justify-center rounded-[var(--ls-radius-round)] text-[var(--tls-fg-subtle)] transition-colors hover:bg-[var(--tls-surface-hover)] hover:text-[var(--tls-fg-strong)]"
        >
          <X aria-hidden="true" className="size-4" />
        </button>
      </div>
    </div>
  )
}
